import { apiSlice } from "../../../features/api/apiSlice";

export type Permission = { id: number; name: string; guard_name: string };

export type Role = {
  id: number;
  name: string;
  guard_name: string;
  permissions?: Permission[];
};

export type ExecutiveOfficeLite = {
  id: number;
  name: string;
  slug?: string;
};
export type MemberSearchResult = {
  id: string; // member_id (UUID)
  membership_no?: string;
  surname?: string;
  firstname?: string;
  othernames?: string | null;
  email?: string;
};

export type ExecutiveOffice = {
  id: number;
  name: string;
  slug?: string;
  description?: string | null;
  rank?: number | null;
  role_id?: number | null;

  role?: {
    id: number;
    name: string;
    guard_name: string;
    permissions?: {
      id: number;
      name: string;
      guard_name: string;
    }[];
  };

  executives?: ExecutiveMember[];
  necs?: any[];
};

export type ExecutiveMember = {
  id: number;
  member_id: string;
  executive_set_id: number;
  executive_office_id: number;
  position?: string | null;
  created_at?: string;
  updated_at?: string;

  member?: {
    id: string;
    membership_no?: string;
    surname?: string;
    firstname?: string;
    othernames?: string | null;
    email?: string;
    phone?: string;
  };

  executive_set?: {
    id: number;
    name: string;
    start_date?: string;
    end_date?: string;
  };
};

export type AssignMemberPayload = {
  member_id: string; // uuid
  office_id: number;
  executive_set_id: number;
  position?: string;
};

function pick(res: any) {
  console.log(res, "thes was returndd");
  return res?.data?.data ?? res?.data ?? res;
}

export const executiveOfficeApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getExecutiveOffices: builder.query<ExecutiveOffice[], void>({
      query: () => "/executive-offices",
      transformResponse: (res: any) => pick(res) || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((o) => ({
                type: "ExecutiveOffices" as const,
                id: o.id,
              })),
              { type: "ExecutiveOffices" as const, id: "LIST" },
            ]
          : [{ type: "ExecutiveOffices" as const, id: "LIST" }],
    }),

    getExecutiveOfficeDetails: builder.query<ExecutiveOffice, number>({
      query: (id) => `/executive-offices/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_res, _err, id) => [
        { type: "ExecutiveOffices" as const, id },
      ],
    }),

    createExecutiveOffice: builder.mutation<
      any,
      { name: string; description?: string; rank?: number }
    >({
      query: (body) => ({
        url: "/executive-offices",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "ExecutiveOffices" as const, id: "LIST" }],
    }),

    updateExecutiveOffice: builder.mutation<
      any,
      {
        id: number;
        name?: string;
        description?: string | null;
        rank?: number | null;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/executive-offices/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "ExecutiveOffices" as const, id },
        { type: "ExecutiveOffices" as const, id: "LIST" },
      ],
    }),

    deleteExecutiveOffice: builder.mutation<any, number>({
      query: (id) => ({
        url: `/executive-offices/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "ExecutiveOffices" as const, id: "LIST" }],
    }),

    assignMemberToOffice: builder.mutation<any, AssignMemberPayload>({
      query: (body) => ({
        url: "/executive-offices/assign-member",
        method: "POST",
        body,
      }),
      // Assignment affects set executives more than office list,
      // but keep this to update office details if needed.
      invalidatesTags: [{ type: "ExecutiveOffices" as const, id: "LIST" }],
    }),

    // member search
    searchMembers: builder.query<MemberSearchResult[], string>({
      query: (q) => `/members/searchm/${encodeURIComponent(q)}`,
      transformResponse: (res: any): MemberSearchResult[] => {
        const arr = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : [];
        return arr.map((m: any) => ({
          id: m.id ?? m.member_id,
          membership_no: m.membership_no,
          surname: m.surname,
          firstname: m.firstname ?? m.other_names,
          othernames: m.othernames ?? null,
          email: m.email,
          phone: m.phone,
        }));
      },
    }),

    // ✅ NEW
    removeExecutive: builder.mutation<any, number>({
      query: (executiveId) => ({
        url: `/executive-offices/executives/${executiveId}`,
        method: "DELETE",
      }),
    }),

    // ✅ NEW
    moveExecutive: builder.mutation<
      any,
      { executiveId: number; executive_office_id: number; position?: string }
    >({
      query: ({ executiveId, ...body }) => ({
        url: `/executive-offices/executives/${executiveId}/move`,
        method: "POST",
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetExecutiveOfficesQuery,
  useSearchMembersQuery,
  useGetExecutiveOfficeDetailsQuery,
  useCreateExecutiveOfficeMutation,
  useUpdateExecutiveOfficeMutation,
  useDeleteExecutiveOfficeMutation,
  useAssignMemberToOfficeMutation,
  useRemoveExecutiveMutation,
  useMoveExecutiveMutation,
} = executiveOfficeApi;
